import React, { useEffect,useRef, useState } from 'react';
import roomServices from '../../core/services/roomServices';

const to12HourFormat = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

const TIME_SLOTS = [
  { start: '00:00', end: '01:00' },
  { start: '01:00', end: '02:00' },
  { start: '02:00', end: '03:00' },
  { start: '03:00', end: '04:00' },
  { start: '04:00', end: '05:00' },
  { start: '05:00', end: '06:00' },
  { start: '06:00', end: '07:00' },
  { start: '07:00', end: '08:00' },
  { start: '08:00', end: '09:00' },
  { start: '09:00', end: '10:00' },
  { start: '10:00', end: '11:00' },
  { start: '11:00', end: '12:00' },
  { start: '12:00', end: '13:00' },
  { start: '13:00', end: '14:00' },
  { start: '14:00', end: '15:00' },
  { start: '15:00', end: '16:00' },
  { start: '16:00', end: '17:00' },
  { start: '17:00', end: '18:00' },
  { start: '18:00', end: '19:00' },
  { start: '19:00', end: '20:00' },
  { start: '20:00', end: '21:00' },
  { start: '21:00', end: '22:00' },
  { start: '22:00', end: '23:00' },
  { start: '23:00', end: '23:59' },
];


// ── Robust list extractor — handles every known response shape ────────────────
function extractRoomList(response) {
  if (!response) return [];

  // Plain array
  if (Array.isArray(response)) return response;

  // Nested .data.data  (axios-style double-wrap)
  if (Array.isArray(response?.data?.data)) return response.data.data;

  // Single .data wrapper
  if (Array.isArray(response?.data)) return response.data;

  // HMgmtResponse field names used across all controllers
  const knownFields = [
    'roomList', 'list', 'items', 'results',
    'data', 'rooms', 'roomlist', 'RoomList',
  ];
  for (const f of knownFields) {
    if (Array.isArray(response?.[f]) ) return response[f];
    if (Array.isArray(response?.data?.[f])) return response.data[f];
  }

  // Last resort: first non-empty array on the object
  if (typeof response === 'object') {
    for (const key of Object.keys(response)) {
      if (Array.isArray(response[key]) && response[key].length > 0) {
        console.log(`[MultiRoomAvailabilityTable] rooms found under key "${key}"`);
        return response[key];
      }
    }
    // Even an empty array is valid
    for (const key of Object.keys(response)) {
      if (Array.isArray(response[key])) return response[key];
    }
  }

  return [];
}

function extractOccupancy(response) {
  if (!response) return {};

  // { success: true, data: { roomId: { date: [...] } } }  ← your backend shape
  if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
    // Check it's actually roomId-keyed (not another wrapper)
    const keys = Object.keys(response.data);
    if (keys.length > 0 && keys[0] !== 'success' && keys[0] !== 'message') {
      return response.data;
    }
  }

  // axios double-wrap: { data: { data: { roomId: ... } } }
  if (response?.data?.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
    return response.data.data;
  }

  if (typeof response === 'object' && !Array.isArray(response)) return response;
  return {};
}

// ── Main Component ────────────────────────────────────────────────────────────
const MultiRoomAvailabilityTable = ({
  date,
  totalDays,
  onSelectRoom,
  selectedRoomId,
  selectedTime,
  currentBookingId,
  roomCategory = '',
}) => {
  const [rooms, setRooms] = useState([]);
  const [occupancy, setOccupancy] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const selectedCellRef = useRef(null);

  useEffect(() => {
    if (date && totalDays) fetchAllRoomOccupancy();
  }, [date, totalDays, roomCategory]);

  // Scroll to the selected room and time after the table renders.
  useEffect(() => {
    if (!selectedRoomId || !selectedTime) return;

    const timer = setTimeout(() => {
      selectedCellRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [selectedRoomId, selectedTime, rooms]);

  const fetchAllRoomOccupancy = async () => {
    setLoading(true);
    setError('');
    try {
      const [rRes, oRes] = await Promise.all([
        roomServices.getRoomList(
          '',
          roomCategory ? [roomCategory] : [],
        ),
        roomServices.getRoomOccupancy(date, totalDays),
      ]);

      // Debug — remove once shapes are confirmed
      console.log('[MultiRoomAvailabilityTable] raw room response:', rRes);
      console.log('[MultiRoomAvailabilityTable] raw occupancy response:', oRes);

      const allRooms = extractRoomList(rRes);
      console.log('[MultiRoomAvailabilityTable] rooms extracted:', allRooms.length, allRooms[0] ?? 'empty');

      // Deduplicate rooms by ID to prevent duplicate room names
      const uniqueRoomsMap = new Map();
      allRooms.forEach((room) => {
        const roomId = String(getRoomId(room));
        if (!uniqueRoomsMap.has(roomId)) {
          uniqueRoomsMap.set(roomId, room);
        }
      });
      const uniqueRooms = Array.from(uniqueRoomsMap.values());
      console.log('[MultiRoomAvailabilityTable] rooms after deduplication:', uniqueRooms.length);

      // Show all rooms; filter by ACTIVE only if a status field exists
      const visibleRooms = uniqueRooms.filter((room) => {
        if (!room.status) return true;
        const s = String(room.status).toUpperCase();
        return s === 'ACTIVE' || s === 'AVAILABLE' || room.status === 1;
      });

      setRooms(visibleRooms);
      setOccupancy(extractOccupancy(oRes));

      if (visibleRooms.length === 0) {
        console.warn('[MultiRoomAvailabilityTable] No rooms after filtering. All rooms:', allRooms);
      }
    } catch (err) {
      console.error('[MultiRoomAvailabilityTable] fetch failed:', err);
      setError('Failed to load room availability. Please try again.');
      setOccupancy({});
    } finally {
      setLoading(false);
    }
  };

const getSlotDetails = (roomId, slotStart, slotEnd) => {
  const roomData = occupancy[String(roomId)];

  if (!roomData || typeof roomData !== 'object') {
    return {
      busy: false,
      subSlots: [
        {
          status: 'FREE',
          start: slotStart,
          end: slotEnd,
        },
      ],
    };
  }

  const bookings = [];

  Object.values(roomData).forEach((slots) => {
    if (!Array.isArray(slots)) return;

    slots.forEach((s) => {
      // Skip the current booking being edited - it should show as SELECTED, not BOOKED
      const bookingId = s.booking_id ?? s.bookingId ?? s.id ?? s.visitId ?? s.visit_id ?? null;
      if (currentBookingId && bookingId != null && String(bookingId) === String(currentBookingId)) return;

      const start = (s.start_time || '').slice(0, 5);
      const end = (s.end_time || '').slice(0, 5);
      const bookStatus = String(s.bookstatus || '').toLowerCase();

      if (bookStatus === 'booked' && !end) {
        bookings.push({ start: slotStart, end: slotEnd });
        return;
      }

      if (
        start &&
        end &&
        slotStart < end &&
        slotEnd > start
      ) {
        bookings.push({ start, end });
      }
    });
  });

  if (bookings.length === 0) {
    return {
      busy: false,
      subSlots: [
        {
          status: 'FREE',
          start: slotStart,
          end: slotEnd,
        },
      ],
    };
  }

  bookings.sort((a, b) => a.start.localeCompare(b.start));

  const segments = [];
  let currentTime = slotStart;

  bookings.forEach((b) => {
    if (b.start > currentTime) {
      segments.push({
        status: 'FREE',
        start: currentTime,
        end: b.start,
      });
    }

    const segmentEnd =
      b.end < slotEnd ? b.end : slotEnd;

    if (segmentEnd > currentTime) {
      segments.push({
        status: 'BOOKED',
        start:
          b.start > currentTime
            ? b.start
            : currentTime,
        end: segmentEnd,
      });

      currentTime = segmentEnd;
    }
  });

  if (currentTime < slotEnd) {
    segments.push({
      status: 'FREE',
      start: currentTime,
      end: slotEnd,
    });
  }

  const dates = Object.keys(roomData);

  const finalSlots = segments.map((seg) => {
    if (seg.status === 'FREE') {
      return seg;
    }

    const conflictDays = [];

    dates.forEach((date) => {
      const daySlots = roomData[date] || [];

      const overlap = daySlots.some((s) => {
        const start = (s.start_time || '').slice(0, 5);
        const end = (s.end_time || '').slice(0, 5);

        return seg.start < end && seg.end > start;
      });

      if (overlap) {
        conflictDays.push(
          new Date(date).toLocaleDateString(
            'en-GB',
            {
              day: '2-digit',
              month: 'short',
            }
          )
        );
      }
    });

    return {
      ...seg,
      conflictDays,
    };
  });

  return {
    busy: true,
    subSlots: finalSlots,
  };
};

//     const bookings = [];
// const roomDataValues = Object.values(roomData);

// roomDataValues.forEach((dateOrSlots) => {
//   // Handle both: { "2025-06-05": [...] } and directly [...]
//   const slots = Array.isArray(dateOrSlots)
//     ? dateOrSlots
//     : Array.isArray(Object.values(dateOrSlots ?? {})[0])
//       ? Object.values(dateOrSlots).flat()
//       : [];

//   slots.forEach((s) => {
//     const start = (s.start_time || '').slice(0, 5);
//     const end   = (s.end_time   || '').slice(0, 5);
//     if (start && end && slotStart < end && slotEnd > start) {
//       bookings.push({ start, end });
//     }
//   });
// });

  //   if (bookings.length === 0) {
  //     return { busy: false, subSlots: [{ status: 'FREE', start: slotStart, end: slotEnd }] };
  //   }

  //   bookings.sort((a, b) => a.start.localeCompare(b.start));
  //   const segments = [];
  //   let currentTime = slotStart;

  //   bookings.forEach((b) => {
  //     if (b.start > currentTime) {
  //       segments.push({ status: 'FREE', start: currentTime, end: b.start });
  //     }
  //     const segmentEnd = b.end < slotEnd ? b.end : slotEnd;
  //     if (segmentEnd > currentTime) {
  //       segments.push({
  //         status: 'BOOKED',
  //         start: b.start > currentTime ? b.start : currentTime,
  //         end: segmentEnd,
  //       });
  //       currentTime = segmentEnd;
  //     }
  //   });

  //   if (currentTime < slotEnd) {
  //     segments.push({ status: 'FREE', start: currentTime, end: slotEnd });
  //   }

  //   const dates = Object.keys(roomData).sort();
  //   const finalSlots = segments.map((seg) => {
  //     if (seg.status === 'FREE') return seg;
  //     const conflictDays = [];
  //     dates.forEach((d) => {
  //       const daySlots = roomData[d] || [];
  //       const hasOverlap = daySlots.some((s) => {
  //         const sStart = (s.start_time || '').slice(0, 5);
  //         const sEnd   = (s.end_time   || '').slice(0, 5);
  //         return seg.start < sEnd && seg.end > sStart;
  //       });
  //       if (hasOverlap) {
  //         conflictDays.push(
  //           new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  //         );
  //       }
  //     });
  //     return { ...seg, conflictDays };
  //   });

  //   return { busy: true, subSlots: finalSlots };
  // };

  // ── Room label helper — handles different field name conventions ──────────
  const getRoomLabel = (room) => {
    const code = room.roomCode || room.room_code || room.code || '';
    const name = room.roomName || room.room_name || room.name || '';
    if (code && name) return `${code} · ${name}`;
    return code || name || `Room ${room.id}`;
  };

  // ── Room ID helper — same as getEntityId in BookingNewSession ─────────────
  const getRoomId = (room) =>
    room?.id ?? room?.room_id ?? room?.roomId ?? room?.code ?? '';

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        <div className="spinner spinner-dark" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 13 }}>Loading room availability…</div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{
        padding: '24px', textAlign: 'center',
        background: '#fef2f2', border: '1px solid #fecaca',
        borderRadius: 12, color: '#b91c1c', fontSize: 13,
      }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{error}</div>
        <button
          onClick={fetchAllRoomOccupancy}
          style={{
            marginTop: 8, padding: '6px 16px', background: '#b91c1c',
            color: 'white', border: 'none', borderRadius: 6,
            cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (rooms.length === 0) {
    return (
      <div style={{
        padding: '40px 20px', textAlign: 'center',
        background: '#f8fafc', border: '1px dashed #cbd5e1',
        borderRadius: 12, color: '#94a3b8',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🏠</div>
        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>No rooms available</div>
        <div style={{ fontSize: 13 }}>No active rooms found for the selected date</div>
      </div>
    );
  }

  // ── Grid ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="availability-grid-wrap"
      style={{
        maxHeight: '560px',
        overflow: 'auto',
        border: '1px solid #cbd5e1',
        borderRadius: 8,
        background: 'white',
        boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
      }}
    >
      <table style={{ fontSize: '0.7rem', borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
        <thead>
          <tr>
            <th style={{
              position: 'sticky', left: 0, top: 0, zIndex: 100,
              background: '#f1f5f9', padding: '12px', textAlign: 'left',
              borderBottom: '1px solid #cbd5e1', borderRight: '1px solid #cbd5e1',
              minWidth: '150px', color: '#0f172a',
            }}>
              ROOM / HOURS
            </th>
            {TIME_SLOTS.map((t) => (
              <th key={t.start} style={{
                position: 'sticky', top: 0, zIndex: 50,
                background: '#f1f5f9', padding: '10px 6px', textAlign: 'center',
                borderBottom: '1px solid #cbd5e1', borderRight: '1px solid #e2e8f0',
                fontWeight: 800, minWidth: '112px', color: '#334155',
              }}>
                {to12HourFormat(t.start)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rooms.map((room) => {
            const roomId = getRoomId(room);
            return (
              <tr key={roomId}>
                <td style={{
                  position: 'sticky', left: 0, zIndex: 40,
                  background: 'white', fontWeight: 800,
                  padding: '12px', borderRight: '1px solid #cbd5e1',
                  borderBottom: '1px solid #e2e8f0',
                  color: '#0f172a',
                  minWidth: '150px',
                }}>
                  {getRoomLabel(room)}
                </td>

                {TIME_SLOTS.map((t) => {
                  const { subSlots } = getSlotDetails(roomId, t.start, t.end);
                  const isSelected =
  String(selectedRoomId) === String(roomId) &&
  selectedTime >= t.start && selectedTime < t.end; // ✅ range check, not exact match

                  return (
                    <td
                      key={t.start}
                      ref={isSelected ? selectedCellRef : undefined} 
                      style={{
                        padding: 0,
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        minWidth: '112px',
                        height: '68px',
                        position: 'relative',
                      }}
                    >
                      <div style={{ display: 'flex', height: '100%', width: '100%' }}>
                        {subSlots.map((ss, idx) => (
                          <div
                            key={idx}
                            onClick={() =>
                              ss.status === 'FREE' && onSelectRoom(roomId, ss.start, ss.end)
                            }
                            style={{
                              flex: 1,
                              background:
                                ss.status === 'BOOKED'
                                  ? '#fee2e2'
                                  : isSelected
                                  ? '#2563eb'
                                  : '#dcfce7',
                              color:
                                ss.status === 'BOOKED'
                                  ? '#b91c1c'
                                  : isSelected
                                  ? 'white'
                                  : '#15803d',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: ss.status === 'BOOKED' ? 'not-allowed' : 'pointer',
                              borderRight:
                                idx < subSlots.length - 1
                                  ? '1px dashed rgba(0,0,0,0.1)'
                                  : 'none',
                              padding: '4px',
                              textAlign: 'center',
                              transition: 'all 0.2s',
                              minWidth: 0,
                            }}
                          >
                            <span style={{ fontSize: '0.6rem', fontWeight: 800 }}>
                              {ss.status === 'BOOKED'
                                ? 'BOOKED'
                                : isSelected
                                ? 'SELECTED'
                                : 'FREE'}
                            </span>
                            <span style={{ fontSize: '0.5rem', opacity: 0.8, marginTop: '2px' }}>
                              {ss.start && ss.end ? `${to12HourFormat(ss.start)}–${to12HourFormat(ss.end)}` : ''}
                              {ss.status === 'BOOKED' && ss.conflictDays?.length > 0 ? ` · ${ss.conflictDays.join(', ')}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};



export default MultiRoomAvailabilityTable;
